const $$splitComponentImporter = () => import('/home/runner/work/vision-lab-frontend/vision-lab-frontend/src/routes/_authenticated/_app/raw-footage/organizations/$organizationId/projects/$projectId.tsx?tsr-split=component');
const $$splitErrorComponentImporter = () => import('/home/runner/work/vision-lab-frontend/vision-lab-frontend/src/routes/_authenticated/_app/raw-footage/organizations/$organizationId/projects/$projectId.tsx?tsr-split=errorComponent');
import { lazyRouteComponent } from '@tanstack/react-router';
import { createCapabilityChecker } from '@/features/authentication/lib/authorizationCapabilities';
import { requireCapabilities } from '@/features/authentication/lib/routeGuards';
import { availableOperationsQuery } from '@/features/authentication/services/permissionQueries';
import { organizationProjectsInfiniteQuery, organizationQuery, projectQuery } from '@/features/organization-project-picker/services/organizationProjectQueries';
import { ProjectDetailSkeleton } from '@/features/raw-footage/components/ProjectDetailSkeleton';
import { projectAssetSearchSchema } from '@/features/raw-footage/lib/projectAssetSearch';
import { projectAssetsQuery } from '@/features/raw-footage/services/projectQueries';
import { createFileRoute } from '@tanstack/react-router';

/**
 * Project detail. A cold route entry prefetches the project and its first asset
 * page into the query cache, so this route owns the loading skeleton
 * (`pendingComponent`) and error UI (`errorComponent`); `ProjectDetail` reads from
 * that warm cache and advances asset pagination in-component. Search changes keep
 * the loaded rows mounted and are owned by the component query. Nested under the
 * org, so the owning org stays in the URL (the sidebar shows the project's org
 * context). The header hosts the "Upload" action (web upload center —
 * `features/uploads`).
 *
 * The loader also warms the org + org-projects queries the persistent sidebar reads
 * via suspense — the sidebar lives outside this route's `Outlet`, so without a warm
 * cache it would re-suspend and flash `SidebarListSkeleton` on every entry here
 * (deep-link, refresh, or crossing an org boundary).
 */
export const Route = createFileRoute('/_authenticated/_app/raw-footage/organizations/$organizationId/projects/$projectId')({
  beforeLoad: requireCapabilities(has => has('assets.list')),
  validateSearch: projectAssetSearchSchema,
  loaderDeps: ({
    search
  }) => ({
    q: search.q
  }),
  loader: async ({
    cause,
    context,
    deps,
    params
  }) => {
    const project = projectQuery(params.projectId);
    const hasCachedProject = context.queryClient.getQueryState(project.queryKey)?.data !== undefined;

    // Search updates create a new loader-dependency match. Once the project is
    // warm, let the component's infinite query handle those changes so a slow or
    // failed search cannot replace already-loaded rows with route-level UI.
    if (cause === 'stay' && hasCachedProject) return;
    const availableOperations = await context.queryClient.ensureQueryData(availableOperationsQuery(context.authUid));
    const includeFieldLog = createCapabilityChecker(availableOperations)('metadata-library.organization.read');
    return Promise.all([context.queryClient.ensureQueryData(organizationQuery(params.organizationId)), context.queryClient.ensureInfiniteQueryData(organizationProjectsInfiniteQuery(params.organizationId)), context.queryClient.ensureQueryData(project), context.queryClient.ensureInfiniteQueryData(projectAssetsQuery(params.projectId, {
      mediaType: 'all',
      q: deps.q,
      includeFieldLog
    }))]);
  },
  shouldReload: false,
  pendingComponent: () => <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-4">
      <ProjectDetailSkeleton />
    </div>,
  errorComponent: lazyRouteComponent($$splitErrorComponentImporter, 'errorComponent'),
  component: lazyRouteComponent($$splitComponentImporter, 'component')
});